/**
 * Base Network Service
 * 
 * Core network infrastructure and common patterns for network monitoring.
 * Provides foundational types, configuration management, and abstract methods
 * for consistent network service implementation.
 */

import { ConnectionStatus, ConnectionType } from '@capacitor/network';

/**
 * Network change events
 */
export type NetworkEvent = 
  | { type: 'network_connected'; data: { connectionType: ConnectionType; timestamp: string } }
  | { type: 'network_disconnected'; data: { lastConnectionType: ConnectionType; timestamp: string } }
  | { type: 'connection_type_changed'; data: { from: ConnectionType; to: ConnectionType; timestamp: string } }
  | { type: 'network_quality_changed'; data: { quality: 'excellent' | 'good' | 'poor' | 'offline'; latency: number } }
  | { type: 'sync_triggered'; data: { trigger: string; networkStatus: ConnectionStatus } };

export type NetworkEventListener = (event: NetworkEvent) => void;

/**
 * Network configuration
 */
export interface NetworkConfig {
  // Sync triggers
  syncOnNetworkRestore: boolean;
  syncOnConnectionTypeChange: boolean;
  syncOnQualityImprovement: boolean;
  
  // Quality monitoring
  enableQualityMonitoring: boolean;
  qualityCheckInterval: number; // milliseconds
  qualityCheckTimeout: number; // milliseconds
  qualityCheckUrl: string;
  
  // Connection type preferences
  preferredConnectionTypes: ConnectionType[];
  avoidMeteredConnections: boolean;
  
  // Timing settings
  networkRestoreDelay: number; // delay before triggering sync after network restore
  connectionChangeDelay: number; // delay before triggering sync after connection type change
  
  // Debug settings
  enableDebugLogging: boolean;
}

/**
 * Network quality metrics
 */
export interface NetworkQuality {
  quality: 'excellent' | 'good' | 'poor' | 'offline';
  latency: number; // milliseconds
  timestamp: string;
  connectionType: ConnectionType;
}

/**
 * Network statistics
 */
export interface NetworkStats {
  totalConnectedTime: number;
  totalDisconnectedTime: number;
  connectionChanges: number;
  qualityChanges: number;
  lastConnectedTime: string | null;
  lastDisconnectedTime: string | null;
  averageLatency: number;
  connectionTypeHistory: Array<{ type: ConnectionType; timestamp: string; duration: number }>;
}

/**
 * Default network configuration
 */
export const DEFAULT_NETWORK_CONFIG: NetworkConfig = {
  syncOnNetworkRestore: true,
  syncOnConnectionTypeChange: false,
  syncOnQualityImprovement: true,
  enableQualityMonitoring: true,
  qualityCheckInterval: 30000, // 30 seconds
  qualityCheckTimeout: 5000, // 5 seconds
  qualityCheckUrl: '/api/health',
  preferredConnectionTypes: ['wifi', 'cellular'],
  avoidMeteredConnections: false,
  networkRestoreDelay: 1000, // 1 second
  connectionChangeDelay: 2000, // 2 seconds
  enableDebugLogging: true,
};

/**
 * Base Network Service
 * 
 * Abstract base class providing core network infrastructure and common patterns.
 * Subclasses implement specific network monitoring and quality detection logic.
 */
export abstract class BaseNetworkService {
  protected config: NetworkConfig;
  protected isInitialized = false;
  
  // Network state
  protected currentStatus: ConnectionStatus | null = null;
  protected connectedStartTime: number | null = null;
  protected disconnectedStartTime: number | null = null;
  
  // Statistics
  protected stats: NetworkStats = {
    totalConnectedTime: 0,
    totalDisconnectedTime: 0,
    connectionChanges: 0,
    qualityChanges: 0,
    lastConnectedTime: null,
    lastDisconnectedTime: null,
    averageLatency: 0,
    connectionTypeHistory: []
  };

  constructor(config: Partial<NetworkConfig> = {}) {
    this.config = {
      ...DEFAULT_NETWORK_CONFIG,
      ...config
    };
  }

  /**
   * Initialize the network service
   */
  abstract initialize(): Promise<void>;

  /**
   * Cleanup resources
   */
  abstract cleanup(): void;

  /**
   * Get current network status
   */
  getCurrentStatus(): ConnectionStatus | null {
    return this.currentStatus;
  }

  /**
   * Get network statistics
   */
  getStats(): NetworkStats {
    // Update current session time if connected
    if (this.currentStatus?.connected && this.connectedStartTime) {
      const currentConnectedTime = Date.now() - this.connectedStartTime;
      return {
        ...this.stats,
        totalConnectedTime: this.stats.totalConnectedTime + currentConnectedTime
      };
    }

    return { ...this.stats };
  }

  /**
   * Reset statistics
   */
  resetStats(): void {
    this.stats = {
      totalConnectedTime: 0,
      totalDisconnectedTime: 0,
      connectionChanges: 0,
      qualityChanges: 0,
      lastConnectedTime: null,
      lastDisconnectedTime: null,
      averageLatency: 0,
      connectionTypeHistory: []
    };
  }

  /**
   * Update configuration
   */
  updateConfig(updates: Partial<NetworkConfig>): void {
    this.config = { ...this.config, ...updates };
  }

  /**
   * Get current configuration
   */
  getConfig(): NetworkConfig {
    return { ...this.config };
  }

  /**
   * Check if service is initialized
   */
  isServiceInitialized(): boolean {
    return this.isInitialized;
  }

  /**
   * Protected method to update connection timing stats
   */
  protected updateConnectionStats(connected: boolean, timestamp: string): void {
    if (connected) {
      // Update disconnected time if we were disconnected
      if (this.disconnectedStartTime) {
        const disconnectedTime = Date.now() - this.disconnectedStartTime;
        this.stats.totalDisconnectedTime += disconnectedTime;
      }
      
      this.connectedStartTime = Date.now();
      this.stats.lastConnectedTime = timestamp;
    } else {
      // Update connected time if we were connected
      if (this.connectedStartTime && this.currentStatus) {
        const connectedTime = Date.now() - this.connectedStartTime;
        this.stats.totalConnectedTime += connectedTime;
        
        // Add to connection type history
        this.stats.connectionTypeHistory.push({
          type: this.currentStatus.connectionType,
          timestamp: this.stats.lastConnectedTime || timestamp,
          duration: connectedTime
        });
      }
      
      this.disconnectedStartTime = Date.now();
      this.stats.lastDisconnectedTime = timestamp;
    }

    this.stats.connectionChanges++;
  }

  /**
   * Protected method to log debug messages
   */
  protected debugLog(message: string, data?: unknown): void {
    if (this.config.enableDebugLogging) {
      if (data) {
        console.log(message, data);
      } else {
        console.log(message);
      }
    }
  }
}
